import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ScatterChart, Scatter, ReferenceLine,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL, fmtPct } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { KPIStatCard, type CardStatus } from "../components/KPIStatCard";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { LoadMoreRow, useProgressiveList } from "../components/LoadMoreRow";
import { StatusPill } from "../components/StatusPill";
import { IconAlertTriangle } from "../components/icons";
import type { TransportMarginByPlateDto } from "@bi/types";
import { SEM_EXTRATO_TITLE } from "../components/TollCoverageNotice";

const fmt2 = (v: number) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const COMP_COLOR: Record<string, string> = {
  "Receita Total": "var(--brand)", "Combustível": "#DAA72B", "Manutenção": "#E8A33D",
  "Motorista": "#8B5CF6", "Seguro de Carga": "#3B82F6", "Margem Final": "var(--brand)",
};
const marginStatus = (pct: number | null): CardStatus =>
  pct == null ? "neutral" : pct >= 0.12 ? "good" : pct >= 0.06 ? "warning" : "critical";

export function MarginByPlatePage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading } = useQuery({
    queryKey: ["transport", "margin", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportMarginByPlateDto>(
        `/dashboards/transport/margin?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ).catch(() => null),
    staleTime: 60_000,
  });

  const s = data?.summary;
  const prev = data?.prevSummary;
  const cmp = data?.comparisonLabel ?? "vs. período anterior";
  const composition = data?.composition ?? [];
  const byPlate = data?.byPlate ?? [];
  const worst = data?.worstMargin ?? [];
  const worstConsumption = data?.worstConsumption ?? [];
  const attention = data?.attention ?? [];

  const revDelta    = computeDelta(s?.totalRevenue, prev?.totalRevenue, { direction: "higher_is_better" });
  const marginDelta = computeDelta(s?.totalMargin, prev?.totalMargin, { direction: "higher_is_better" });
  const rpkDelta    = computeDelta(s?.revenuePerKm ?? null, prev?.revenuePerKm ?? null, { direction: "higher_is_better" });
  const ppText = (s?.avgMarginPct != null && prev?.avgMarginPct != null)
    ? `${(s.avgMarginPct - prev.avgMarginPct) >= 0 ? "↑" : "↓"} ${fmt2(Math.abs((s.avgMarginPct - prev.avgMarginPct) * 100))} p.p. ${cmp}`
    : cmp;

  const fleetRpk = s?.revenuePerKm ?? 0;
  const plateList = useProgressiveList(byPlate);
  const top10 = byPlate.filter((p) => p.margin > 0).slice(0, 10).map((p) => ({ plate: p.plate, margin: p.margin }));
  const scatter = byPlate.filter((p) => p.km > 0 && p.revenuePerKm != null && p.costPerKm != null)
    .map((p) => ({ x: p.costPerKm as number, y: p.revenuePerKm as number, plate: p.plate,
      above: (p.marginPct ?? -1) >= (s?.avgMarginPct ?? 0) }));
  const scatterAbove = scatter.filter((d) => d.above);
  const scatterBelow = scatter.filter((d) => !d.above);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Margem por Placa"
        subtitle="Rentabilidade por veículo com faturamento, custos diretos e margem operacional"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPIStatCard label="Faturamento Total" memory={{ cardKey: "margem.faturamento", from, to, expected: s?.totalRevenue }} value={s ? fmtBRL(s.totalRevenue) : "—"}
          delta={revDelta} comparisonLabel={cmp} status="neutral" loading={isLoading} />
        <KPIStatCard label="Custo Direto Total" memory={{ cardKey: "margem.custo-direto", from, to, expected: s?.totalDirectCost }} value={s ? fmtBRL(s.totalDirectCost) : "—"}
          subtitle="combustível + manutenção + motorista + pedágio + despesas + seguro" status="neutral" loading={isLoading} />
        <KPIStatCard label="Margem Operacional" memory={{ cardKey: "margem.margem", from, to, expected: s?.totalMargin }} value={s ? fmtBRL(s.totalMargin) : "—"}
          delta={marginDelta} comparisonLabel={cmp}
          status={s ? marginStatus(s.avgMarginPct) : "neutral"} loading={isLoading} />
        <KPIStatCard label="Margem Média" memory={{ cardKey: "margem.margem-media", from, to, expected: s?.avgMarginPct == null ? null : s.avgMarginPct * 100 }} value={s?.avgMarginPct != null ? fmtPct(s.avgMarginPct * 100) : "—"}
          subtitle={ppText} status={s ? marginStatus(s.avgMarginPct) : "neutral"} loading={isLoading} />
        <KPIStatCard label="Receita por KM" memory={{ cardKey: "margem.receita-km", from, to, expected: s?.revenuePerKm }} value={s?.revenuePerKm != null ? `R$ ${fmt2(s.revenuePerKm)}` : "—"}
          delta={rpkDelta} comparisonLabel={cmp} status="neutral" loading={isLoading} />
        <KPIStatCard label="Placa Mais Rentável" memory={{ cardKey: "margem.placa-mais-rentavel", from, to, expected: s?.mostProfitablePlate ? s.mostProfitableMargin : undefined }} value={s?.mostProfitablePlate ?? "—"}
          valueSub={s ? fmtBRL(s.mostProfitableMargin) : undefined} subtitle="maior margem operacional"
          status={s?.mostProfitablePlate ? "good" : "neutral"} loading={isLoading} />
      </div>

      {/* Pontos de atenção + Top 10 + scatter */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-5">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
            <IconAlertTriangle className="w-4 h-4 text-amber-500" />
            Pontos de atenção da rentabilidade ({attention.length})
          </p>
          <div className="space-y-2.5">
            {attention.length === 0 ? (
              <p className="text-sm text-gray-400">Sem pontos de atenção no período.</p>
            ) : attention.map((a, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${a.severity === "critical" ? "bg-red-500" : a.severity === "warning" ? "bg-amber-500" : "bg-blue-500"}`} />
                <p className="font-medium text-gray-800">{a.title}</p>
              </div>
            ))}
          </div>
        </div>

        <ChartCard title="Top 10 placas por margem" subtitle="Por margem operacional (R$)" height={260}
          isEmpty={!isLoading && top10.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top10} layout="vertical" margin={{ top: 0, right: 60, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="plate" tick={{ fontSize: 11, fill: "#6b7280" }} tickLine={false} axisLine={false} width={72} />
              <Tooltip formatter={(v: number) => [fmtBRL(v), "Margem"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }} />
              <Bar dataKey="margin" fill="var(--brand)" radius={[0, 4, 4, 0]}
                label={{ position: "right", formatter: (v: number) => fmtBRL(v), fontSize: 10, fill: "#6b7280" }} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Receita × Custo por KM" subtitle="Cada ponto representa um veículo" height={260}
          isEmpty={!isLoading && scatter.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" dataKey="x" name="Custo/km" tickFormatter={(v) => `R$ ${fmt2(v)}`}
                tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis type="number" dataKey="y" name="Receita/km" tickFormatter={(v) => `R$ ${fmt2(v)}`}
                tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} width={64} />
              {fleetRpk > 0 && <ReferenceLine y={fleetRpk} stroke="#9ca3af" strokeDasharray="6 4"
                label={{ value: "Média da frota", position: "insideTopRight", fontSize: 10, fill: "#9ca3af" }} />}
              <Tooltip cursor={{ strokeDasharray: "3 3" }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
                formatter={(v: number, n: string) => [`R$ ${fmt2(v)}`, n]} labelFormatter={() => ""} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Scatter name="Acima da média" data={scatterAbove} fill="var(--brand)" />
              <Scatter name="Abaixo da média" data={scatterBelow} fill="#E5484D" />
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Composição da margem + piores margens + piores consumos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Composição da margem por categoria" subtitle="Participação no faturamento total" height={220}
          isEmpty={!isLoading && composition.every((c) => c.value === 0)} loading={isLoading}>
          <div className="space-y-3.5 pt-1">
            {composition.map((c) => (
              <div key={c.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className={`text-gray-700 ${c.kind === "margin" ? "font-semibold" : ""}`}>{c.label}</span>
                  <span className="font-semibold text-gray-900">{fmtBRL(c.value)} <span className="text-gray-400 font-normal">({fmtPct(c.pct * 100)})</span></span>
                </div>
                <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.round(c.pct * 100))}%`, background: COMP_COLOR[c.label] ?? "var(--brand)" }} />
                </div>
              </div>
            ))}
          </div>
        </ChartCard>

        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Veículos com pior margem</p>
            <p className="text-xs text-gray-400 mt-0.5">Ranking por margem operacional (%)</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-surface-border">
                  <th className="px-5 py-2 text-left font-medium">#</th>
                  <th className="px-3 py-2 text-left font-medium">Placa</th>
                  <th className="px-3 py-2 text-left font-medium">Motorista</th>
                  <th className="px-3 py-2 text-right font-medium">Margem %</th>
                  <th className="px-5 py-2 text-center font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={5} className="px-5 py-6 text-center text-gray-400">Carregando…</td></tr>
                ) : worst.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-6 text-center text-gray-400">Sem dados no período.</td></tr>
                ) : worst.map((w, i) => (
                  <tr key={w.plate} className="border-b border-surface-border/60 hover:bg-gray-50">
                    <td className="px-5 py-2.5 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-2.5 font-medium">
                      <Link to={`/frota/placa/${encodeURIComponent(w.plate)}`} className="text-tm-700 hover:underline">{w.plate}</Link>
                    </td>
                    <td className="px-3 py-2.5 text-gray-600">{w.driver ?? "—"}</td>
                    <td className="px-3 py-2.5 text-right text-gray-700">{w.marginPct != null ? fmtPct(w.marginPct * 100) : "—"}</td>
                    <td className="px-5 py-2.5 text-center"><StatusPill status={w.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Veículos com pior consumo</p>
            <p className="text-xs text-gray-400 mt-0.5">
              km/l diesel{s?.fleetKmPerLiter != null ? ` · média da frota ${fmt2(s.fleetKmPerLiter)} km/l` : ""} · mínimo 3 abastecimentos e 500 km
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-surface-border">
                  <th className="px-5 py-2 text-left font-medium">#</th>
                  <th className="px-3 py-2 text-left font-medium">Placa</th>
                  <th className="px-3 py-2 text-right font-medium">km/l</th>
                  <th className="px-5 py-2 text-right font-medium">vs. frota</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={4} className="px-5 py-6 text-center text-gray-400">Carregando…</td></tr>
                ) : worstConsumption.length === 0 ? (
                  <tr><td colSpan={4} className="px-5 py-6 text-center text-gray-400">Sem placas com base mínima de abastecimentos no período.</td></tr>
                ) : worstConsumption.map((w, i) => (
                  <tr key={w.plate} className="border-b border-surface-border/60 hover:bg-gray-50">
                    <td className="px-5 py-2.5 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-2.5 font-medium">
                      <Link to={`/frota/placa/${encodeURIComponent(w.plate)}`} className="text-tm-700 hover:underline">{w.plate}</Link>
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-gray-800">{fmt2(w.kmPerLiter)}</td>
                    <td className={`px-5 py-2.5 text-right font-medium ${w.vsFleetPct != null && w.vsFleetPct < 0 ? "text-danger-600" : "text-tm-700"}`}>
                      {w.vsFleetPct != null ? `${w.vsFleetPct >= 0 ? "+" : ""}${fmt2(w.vsFleetPct * 100)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Ranking detalhado por placa */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border">
          <p className="text-sm font-semibold text-gray-900">Ranking detalhado por placa</p>
          <p className="text-xs text-gray-400 mt-0.5">Faturamento, custos diretos e margem operacional por veículo</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 border-b border-surface-border">
                <th className="px-5 py-2 text-left font-medium">Placa</th>
                <th className="px-3 py-2 text-left font-medium">Motorista</th>
                <th className="px-3 py-2 text-right font-medium">Faturamento</th>
                <th className="px-3 py-2 text-right font-medium">Combustível</th>
                <th className="px-3 py-2 text-right font-medium">Manutenção</th>
                <th className="px-3 py-2 text-right font-medium">Motorista</th>
                <th className="px-3 py-2 text-right font-medium">Pedágio</th>
                <th className="px-3 py-2 text-right font-medium">Seguro</th>
                <th className="px-3 py-2 text-right font-medium">Margem R$</th>
                <th className="px-3 py-2 text-right font-medium">Margem %</th>
                <th className="px-3 py-2 text-right font-medium">Receita/KM</th>
                <th className="px-3 py-2 text-right font-medium">Custo/KM</th>
                <th className="px-3 py-2 text-right font-medium">km/l</th>
                <th className="px-5 py-2 text-center font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={13} className="px-5 py-6 text-center text-gray-400">Carregando…</td></tr>
              ) : byPlate.length === 0 ? (
                <tr><td colSpan={13} className="px-5 py-6 text-center text-gray-400">Sem dados no período.</td></tr>
              ) : plateList.visible.map((p) => (
                <tr key={p.plate} className="border-b border-surface-border/60 hover:bg-gray-50">
                  <td className="px-5 py-2.5 font-medium">
                    <Link to={`/frota/placa/${encodeURIComponent(p.plate)}`} className="text-tm-700 hover:underline">{p.plate}</Link>
                    {p.trailers.length > 0 && (
                      <p className="text-xs font-normal text-blue-600" title="Custo da carreta somado nesta linha (vínculo Sinersoft)">
                        + carreta {p.trailers.join(" · ")}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-gray-600">{p.driver ?? "—"}</td>
                  <td className="px-3 py-2.5 text-right text-gray-700">{p.hasRevenue ? p.revenueFormatted : "—"}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{fmtBRL(p.fuel)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{fmtBRL(p.maintenance)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{fmtBRL(p.driverPay + p.commission)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">
                    {p.tollsSource === "sem_extrato"
                      ? <span className="text-xs text-gray-400" title={SEM_EXTRATO_TITLE}>sem extrato</span>
                      : p.tollsSource == null ? <span className="text-gray-300">—</span> : fmtBRL(p.tolls)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{fmtBRL(p.seguro)}</td>
                  <td className={`px-3 py-2.5 text-right font-semibold ${p.margin >= 0 ? "text-tm-700" : "text-danger-600"}`}>{p.marginFormatted}</td>
                  <td className="px-3 py-2.5 text-right text-gray-700">{p.marginPct != null ? fmtPct(p.marginPct * 100) : "—"}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{p.revenuePerKm != null ? `R$ ${fmt2(p.revenuePerKm)}` : "—"}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{p.costPerKm != null ? `R$ ${fmt2(p.costPerKm)}` : "—"}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{p.kmPerLiter != null ? fmt2(p.kmPerLiter) : "—"}</td>
                  <td className="px-5 py-2.5 text-center"><StatusPill status={p.status} /></td>
                </tr>
              ))}
              {!isLoading && <LoadMoreRow list={plateList} colSpan={14} label="placas" />}
            </tbody>
          </table>
        </div>
        <p className="px-5 py-3 text-xs text-gray-400 border-t border-surface-border">
          Margem <strong>parcial</strong>: inclui combustível, manutenção, motorista, pedágio, despesas de viagem e seguro.
          Não inclui pneus, IPVA/licenciamento, financiamento e custos administrativos. km/l considera apenas diesel.
          Motorista e seguro vêm das VIAGENS do período (controle de viagens); placa sem viagem no período aparece
          sem esses custos e o seguro cai na exposição estimada.
          {" "}<strong>Pedágio</strong>: só o PAGO na praça (extrato Sem Parar importado em Frota › Pedágio, passagens
          datadas); período sem extrato aparece como "sem extrato" e não entra no custo.
          {s && s.tollsFromInvoices > 0 && (
            <> Neste período: {fmtBRL(s.tollsFromInvoices)} de extrato em {s.platesWithPaidTolls} placa(s).</>
          )}
          Fonte: Sinersoft (CT-e status 2, abastecimentos, ordens de serviço, viagens). "—" = sem dado no período, não é zero.
        </p>
      </div>
    </div>
  );
}
